import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
    ArrowRight,
    Bot,
    Check,
    CircleDot,
    GitBranch,
    Loader2,
    Search,
    X,
} from "lucide-react";

import axiosInstance from "src/common/axiosInstance";

const nodeIcons = {
    automation: Bot,
    decision: GitBranch,
};

function NodeIcon({ type }) {
    const Icon = nodeIcons[type] || CircleDot;
    return <Icon className="h-4 w-4" />;
}

export default function WorkflowPositionModal({
    open,
    leadId,
    workflow,
    onClose,
    onMoved,
}) {
    const [stages, setStages] = useState([]);
    const [selectedStageId, setSelectedStageId] = useState("");
    const [selectedNodeId, setSelectedNodeId] = useState("");
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!open || !leadId) return undefined;

        const controller = new AbortController();

        async function loadOptions() {
            setLoading(true);
            setError("");
            setSearch("");

            try {
                const response = await axiosInstance.get(
                    `/lead-workflow/${leadId}/workflow/options`,
                    { signal: controller.signal },
                );
                const options = Array.isArray(response.data?.data) ? response.data.data : [];
                const currentStageId = String(workflow?.current_stage?.id || "");
                const currentNodeId = String(workflow?.current_node?.id || "");
                const initialStage = options.find((stage) => String(stage.id) === currentStageId)
                    || options[0]
                    || null;
                const initialNode = initialStage?.nodes?.find((node) => String(node.id) === currentNodeId)
                    || initialStage?.nodes?.[0]
                    || null;

                setStages(options);
                setSelectedStageId(initialStage ? String(initialStage.id) : "");
                setSelectedNodeId(initialNode ? String(initialNode.id) : "");
            } catch (requestError) {
                if (requestError?.code !== "ERR_CANCELED") {
                    setError(requestError?.response?.data?.message || "Unable to load workflow positions.");
                }
            } finally {
                if (!controller.signal.aborted) setLoading(false);
            }
        }

        loadOptions();
        return () => controller.abort();
    }, [leadId, open, workflow?.current_node?.id, workflow?.current_stage?.id]);

    const selectedStage = useMemo(
        () => stages.find((stage) => String(stage.id) === selectedStageId) || null,
        [selectedStageId, stages],
    );
    const visibleNodes = useMemo(() => {
        const query = search.trim().toLowerCase();
        const nodes = Array.isArray(selectedStage?.nodes) ? selectedStage.nodes : [];
        if (!query) return nodes;

        return nodes.filter((node) =>
            `${node.name || ""} ${node.description || ""} ${node.node_type || ""}`
                .toLowerCase()
                .includes(query)
        );
    }, [search, selectedStage]);

    function selectStage(stage) {
        const currentNodeId = String(workflow?.current_node?.id || "");
        const currentNode = stage.nodes?.find((node) => String(node.id) === currentNodeId);

        setSelectedStageId(String(stage.id));
        setSelectedNodeId(String(currentNode?.id || stage.nodes?.[0]?.id || ""));
        setSearch("");
        setError("");
    }

    async function savePosition() {
        if (!selectedStageId || !selectedNodeId) return;

        setSaving(true);
        setError("");

        try {
            await axiosInstance.put(`/lead-workflow/${leadId}/workflow/update-current`, {
                stage_id: Number(selectedStageId),
                node_id: Number(selectedNodeId),
                notes: "Workflow position changed from the lead journey.",
            });
            await onMoved?.();
            onClose();
        } catch (requestError) {
            setError(requestError?.response?.data?.message || "Unable to change the workflow position.");
        } finally {
            setSaving(false);
        }
    }

    if (!open) return null;

    const unchanged = selectedStageId === String(workflow?.current_stage?.id || "")
        && selectedNodeId === String(workflow?.current_node?.id || "");

    return createPortal(
        <div className="fixed inset-0 z-[10020] flex items-center justify-center bg-slate-950/45 p-3 backdrop-blur-sm sm:p-6" role="dialog" aria-modal="true" aria-labelledby="workflow-position-title">
            <div className="flex max-h-[88vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
                <header className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4 sm:px-6">
                    <div>
                        <p className="text-xs font-bold uppercase tracking-[0.14em] text-blue-600">Lead journey</p>
                        <h2 id="workflow-position-title" className="mt-1 text-xl font-semibold tracking-tight text-slate-950">Jump to a stage or activity</h2>
                        <p className="mt-1 text-sm text-slate-500">Choose the exact point where this lead should continue.</p>
                    </div>
                    <button type="button" onClick={onClose} disabled={saving} className="rounded-xl p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700" aria-label="Close workflow position">
                        <X className="h-5 w-5" />
                    </button>
                </header>

                {error && <div role="alert" className="mx-5 mt-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-medium text-rose-700 sm:mx-6">{error}</div>}

                {loading ? (
                    <div className="flex min-h-80 flex-1 items-center justify-center gap-3 text-sm font-medium text-slate-500">
                        <Loader2 className="h-5 w-5 animate-spin text-blue-600" /> Loading pipeline activities…
                    </div>
                ) : stages.length === 0 ? (
                    <div className="flex min-h-80 flex-1 items-center justify-center text-sm font-medium text-slate-500">This pipeline has no available activities.</div>
                ) : (
                    <div className="grid min-h-0 flex-1 md:grid-cols-[300px_minmax(0,1fr)]">
                        <aside className="min-h-0 overflow-y-auto border-b border-slate-200 bg-slate-50/80 p-3 md:border-b-0 md:border-r">
                            <p className="px-2 pb-2 text-xs font-bold uppercase tracking-[0.12em] text-slate-400">Stages</p>
                            <div className="space-y-1.5">
                                {stages.map((stage) => {
                                    const selected = String(stage.id) === selectedStageId;
                                    const current = String(stage.id) === String(workflow?.current_stage?.id || "");
                                    return (
                                        <button key={stage.id} type="button" onClick={() => selectStage(stage)} className={`flex w-full items-center gap-3 rounded-xl border px-3 py-3 text-left transition ${selected ? "border-blue-200 bg-white shadow-sm ring-1 ring-blue-100" : "border-transparent hover:border-slate-200 hover:bg-white"}`}>
                                            <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-xs font-bold ${selected ? "bg-blue-600 text-white" : "bg-slate-200 text-slate-600"}`}>{stage.display_order}</span>
                                            <span className="min-w-0 flex-1">
                                                <span className="block truncate text-sm font-semibold text-slate-900">{stage.name}</span>
                                                <span className="mt-0.5 block text-xs text-slate-500">{stage.nodes?.length || 0} activities</span>
                                            </span>
                                            {current && <span className="rounded-full bg-emerald-50 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-emerald-700">Current</span>}
                                        </button>
                                    );
                                })}
                            </div>
                        </aside>

                        <section className="flex min-h-0 flex-col">
                            <div className="border-b border-slate-100 px-5 py-4 sm:px-6">
                                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                    <div className="min-w-0">
                                        <p className="truncate text-base font-semibold text-slate-950">{selectedStage?.name}</p>
                                        <p className="text-xs text-slate-500">Select the activity that should become current.</p>
                                    </div>
                                    <label className="relative block sm:w-64">
                                        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                                        <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search activities" className="h-10 w-full rounded-xl border border-slate-200 pl-9 pr-3 text-sm outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-50" />
                                    </label>
                                </div>
                            </div>

                            <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-5">
                                <div className="space-y-2">
                                    {visibleNodes.map((node) => {
                                        const selected = String(node.id) === selectedNodeId;
                                        const current = String(node.id) === String(workflow?.current_node?.id || "");
                                        return (
                                            <button key={node.id} type="button" onClick={() => { setSelectedNodeId(String(node.id)); setError(""); }} className={`flex w-full items-center gap-3 rounded-xl border p-3.5 text-left transition ${selected ? "border-blue-300 bg-blue-50/60 ring-2 ring-blue-100" : "border-slate-200 hover:border-blue-200 hover:bg-slate-50"}`}>
                                                <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${selected ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-500"}`}><NodeIcon type={node.node_type} /></span>
                                                <span className="min-w-0 flex-1">
                                                    <span className="flex flex-wrap items-center gap-2">
                                                        <span className="truncate text-sm font-semibold text-slate-900">{node.name}</span>
                                                        <span className="rounded-md bg-white px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-slate-500 ring-1 ring-slate-200">{node.node_type || "manual"}</span>
                                                        {current && <span className="text-[10px] font-bold uppercase tracking-wide text-emerald-700">Current</span>}
                                                    </span>
                                                    {node.description && <span className="mt-1 block truncate text-xs text-slate-500">{node.description}</span>}
                                                </span>
                                                <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border ${selected ? "border-blue-600 bg-blue-600 text-white" : "border-slate-300 text-transparent"}`}><Check className="h-3.5 w-3.5" /></span>
                                            </button>
                                        );
                                    })}
                                    {visibleNodes.length === 0 && <div className="rounded-xl border border-dashed border-slate-200 py-12 text-center text-sm text-slate-500">No activities match your search.</div>}
                                </div>
                            </div>
                        </section>
                    </div>
                )}

                <footer className="flex flex-col-reverse gap-3 border-t border-slate-200 bg-slate-50 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
                    <p className="text-xs text-slate-500">Completed history is preserved. The selected activity becomes the next action.</p>
                    <div className="flex items-center justify-end gap-2">
                        <button type="button" onClick={onClose} disabled={saving} className="h-10 rounded-xl px-4 text-sm font-semibold text-slate-600 transition hover:bg-slate-200/60">Cancel</button>
                        <button type="button" onClick={savePosition} disabled={loading || saving || unchanged || !selectedNodeId} className="inline-flex h-10 items-center gap-2 rounded-xl bg-blue-600 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50">
                            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
                            {saving ? "Moving…" : "Move lead here"}
                        </button>
                    </div>
                </footer>
            </div>
        </div>,
        document.body,
    );
}
